/**
 * Every server path the trader dashboard talks to, in one place.
 *
 * The API is still being finalised by the api-server team, so paths are
 * deliberately centralised here: correcting a route later is a one-line change
 * and never requires touching a screen.
 */

export const API_BASE_URL: string =
  import.meta.env.VITE_API_URL ?? '/api';

export const endpoints = {
  auth: {
    signup: '/traders/auth/signup',
    login: '/traders/auth/login',
    logout: '/traders/auth/logout',
    profile: '/traders/profile',
  },

  account: {
    profile: '/traders/profile',
    changePassword: '/traders/profile/password',
    notificationPrefs: '/traders/profile/notifications',
    twoFaSetup: '/traders/profile/2fa/setup',
    twoFaEnable: '/traders/profile/2fa/enable',
    twoFaDisable: '/traders/profile/2fa/disable',
    sessions: '/traders/profile/sessions',
    revokeSession: (sessionId: string) => `/traders/profile/sessions/${sessionId}`,
  },

  kyc: {
    status: '/traders/kyc',
    passport: '/traders/documents/passport',
    selfie: '/traders/documents/selfie',
    submit: '/traders/kyc/submit',
  },

  cards: {
    list: '/traders/cards',
    create: '/traders/cards',
    remove: (cardId: string) => `/traders/cards/${cardId}`,
    setPrimary: (cardId: string) => `/traders/cards/${cardId}/primary`,
    /** PATCH — daily / per-payment / concurrent ceilings for one card. */
    limits: (cardId: string) => `/traders/cards/${cardId}/limits`,
    /** PATCH `{ isEnabled }` — the trader's own per-card switch. */
    enabled: (cardId: string) => `/traders/cards/${cardId}/enabled`,
  },

  transactions: {
    list: '/traders/transactions',
    byId: (txId: string) => `/traders/transactions/${txId}`,
    confirm: (txId: string) => `/traders/transactions/${txId}/confirm`,
  },

  disputes: {
    create: '/traders/disputes',
  },

  /**
   * The money layer. `balance` below is the older, smaller snapshot the
   * overview still reads; `money.balances` is the ledger-derived one that the
   * Финансы screen is built on. Both are listed so a future removal of the
   * former is a single deletion here.
   */
  money: {
    /** GET -> TraderBalances (deposit / frozen / available / withdrawn). */
    balances: '/traders/balances',
    /** GET ?limit&offset&account&kind -> Paginated<LedgerEntry>. */
    ledger: '/traders/ledger',
    /** GET -> TrafficStatus · PUT `{ isEnabled }` -> TrafficStatus. */
    traffic: '/traders/traffic',
    /** GET ?limit&offset -> Paginated<WithdrawalRequest> · POST -> one. */
    withdrawals: '/traders/withdrawals',
    /** GET -> CommissionRate[] */
    commission: '/traders/commission',
  },

  balance: '/traders/balance',
  stats: '/traders/stats',

  /**
   * Server-sent events. EventSource cannot set an Authorization header, so the
   * access token is passed as a query parameter — this matches the agreed
   * server contract. Treated as a short-lived bearer; rotate on logout.
   */
  notifications: (token: string) =>
    `${API_BASE_URL}/traders/notifications?token=${encodeURIComponent(token)}`,
} as const;
